import { useMemo, useState } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmt } from "@/lib/format";

const COLORS = ["#0B3C5D", "#F5A623", "#10B981", "#EF4444", "#6366F1", "#0EA5E9", "#D946EF", "#84CC16",
  "#F97316", "#14B8A6", "#A855F7", "#E11D48", "#64748B", "#CA8A04", "#2563EB", "#059669"];
const GROUPS = [{ key: "unit", label: "Unit / Project" }, { key: "uid", label: "UID" }, { key: "tipe", label: "Tipe Project" }];
const TOP = 10;

export const MarketShare = ({ share }) => {
  const [metric, setMetric] = useState(share?.metrics?.[0]?.key);
  const [group, setGroup] = useState("unit");
  const m = share?.metrics.find((x) => x.key === metric);
  const data = useMemo(() => {
    if (!share) return [];
    const acc = {};
    share.rows.forEach((r) => { const g = r[group] || "-"; acc[g] = (acc[g] || 0) + (r[metric] || 0); });
    const total = Object.values(acc).reduce((a, v) => a + v, 0) || 1;
    const sorted = Object.entries(acc).map(([name, value]) => ({ name, value, share: value / total })).sort((a, b) => b.value - a.value);
    if (sorted.length <= TOP + 1) return sorted;
    const rest = sorted.slice(TOP);
    return [...sorted.slice(0, TOP), { name: `Lainnya (${rest.length})`, value: rest.reduce((a, d) => a + d.value, 0), share: rest.reduce((a, d) => a + d.share, 0) }];
  }, [share, metric, group]);
  if (!share) return null;
  const total = data.reduce((a, d) => a + d.value, 0);
  const lead = data[0];
  return (
    <div data-testid="market-share" className="rounded-xl border bg-card p-4 sm:p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-heading text-base sm:text-lg font-semibold">Market Share Aset Pengusahaan</h3>
          <p className="text-xs text-muted-foreground">Porsi tiap {GROUPS.find((g) => g.key === group).label.toLowerCase()} terhadap total {m?.label}</p>
        </div>
        <div className="flex gap-2">
          <Select value={metric} onValueChange={setMetric}>
            <SelectTrigger data-testid="share-metric-select" className="h-9 w-48"><SelectValue /></SelectTrigger>
            <SelectContent>{share.metrics.map((x) => <SelectItem key={x.key} value={x.key}>{x.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={group} onValueChange={setGroup}>
            <SelectTrigger data-testid="share-group-select" className="h-9 w-40"><SelectValue /></SelectTrigger>
            <SelectContent>{GROUPS.map((g) => <SelectItem key={g.key} value={g.key}>{g.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="mt-4 grid items-center gap-6 lg:grid-cols-5">
        <div className="relative h-72 lg:col-span-2">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="name" innerRadius="55%" outerRadius="88%" paddingAngle={1}>
                {data.map((d, i) => <Cell key={d.name} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v, n, it) => [`${fmt(v, m?.fmt)} (${fmt(it.payload.share, "pct")})`, n]} contentStyle={{ borderRadius: 10, fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Total</span>
            <span data-testid="share-total" className="font-num text-lg font-bold">{fmt(total, m?.fmt)}</span>
          </div>
        </div>
        <div className="space-y-2 lg:col-span-3">
          {lead && <p data-testid="share-lead" className="text-sm"><b>{lead.name}</b> memimpin dengan porsi <b className="font-num">{fmt(lead.share, "pct")}</b>.</p>}
          {data.map((d, i) => (
            <div key={d.name} data-testid={`share-row-${i}`} className="text-xs">
              <div className="flex justify-between gap-2">
                <span className="flex items-center gap-1.5 truncate"><span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: COLORS[i % COLORS.length] }} />{d.name}</span>
                <span className="font-num whitespace-nowrap">{fmt(d.value, m?.fmt)} · <b>{fmt(d.share, "pct")}</b></span>
              </div>
              <div className="mt-1 h-1.5 rounded-full bg-muted">
                <div className="h-1.5 rounded-full transition-[width] duration-500" style={{ width: `${Math.max(1, d.share * 100)}%`, background: COLORS[i % COLORS.length] }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
